import Typography from '@components/base/typography';
import {
  AutoComplete,
  DataGrid,
  DeletePrompt,
  InputDatePicker,
  PaginationScroller,
  PopOverButton,
  SearchBar,
  SelectLabel,
  TableSkeleton
} from '@components/common';
import DocumentRow from '@components/common/document-row';
import ListPlaceholder from '@components/common/list-placeholder';
import notify from '@helpers/toastify-helper';
import {
  calculateNetAmount,
  getCommaSeperatedCurrencyAmount,
  getFormattedDate
} from '@helpers/utils';
import { deleteInvoice, fetchInvoiceList } from '@services/finance.service';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { CellProps } from 'react-table';
import css from './index.module.scss';
import Button from '@components/base/button';
import DetailModal from './detail-modal';
import { TAG_LIMIT_VIEW } from '@helpers/constants';
import { Chip, ToolTip, IconNode, Divider, Drawer } from '@components/base';
import { useSearchParams } from 'react-router-dom';
import { IInvoiceKeys, PAYMENT_ORDER_BY } from '@pages/finance/home';
import Images from '@assets/images';
import { getSupplierNames } from '@services/supplier.service';
import { fetchEntityList, getReadableOrderIdList } from '@services/order.service';
import { debounce } from 'lodash';
import moment from 'moment';
import UploadOrderDocument from '@components/common/upload-order-document';
import FinanceFilterDrawer, { FinanceFilterDrawerState } from '../filters';

interface IOrderList {
  order_id: string;
  readable_order_id: string;
}

interface ISupplierIdName {
  supplier_id: string;
  supplier_name: string;
}

interface IEntity {
  entity_id: string;
  name: string;
}

interface IInvoiceState {
  isLoading: boolean;
  tableData: any;
  detailModalOpen: boolean;
  detailModalData: any;
  currentPageNumber: number;
  itemLimit: number;
  invoiceCount: number;
  currentStatus: number;
  invoice_order_by: string;
  editModalToggle: boolean;
  openDeletePrompt: boolean;
  delete_invoice_info_id: string | null;
  showFilterDrawer: boolean;
  filters: FinanceFilterDrawerState | null;
}

interface IInvoiceProps {
  order_id?: string;
  setInvoiceKeys?: React.Dispatch<React.SetStateAction<IInvoiceKeys>>;
}

interface IFilterState {
  supplier: ISupplierIdName | null;
  orderId: IOrderList | null;
  startDate: Date | null;
  endDate: Date | null;
  searchText: string | null;
  entity: IEntity | null;
  isSearchLoading: boolean;
  excludeZeroOutstanding: boolean;
}

interface IInvoiceTableParams {
  lower_bound_date: string | null;
  order_by: string;
  order_id: string | null;
  page: number;
  search_text: string | null;
  supplier_id: string | null;
  upper_bound_date: string | null;
  entity_id: string | null;
  non_zero_due_amount: boolean;
}

const InvoicesTab = (props: IInvoiceProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activePage = searchParams.get('page') || '1';
  const debounceSearch = useRef<any>();

  const getValidEntriesLength = (obj: any): number => {
    if (obj == null) return 0;
    return Object.values(obj).filter((value) => {
      if (value === null) return false;
      if (typeof value === 'boolean' && value === false) return false;
      if (typeof value === 'string' && value.trim().length === 0) return false;
      return true;
    }).length;
  };

  const [invoicesState, setInvoicesState] = useState<IInvoiceState>({
    isLoading: false,
    tableData: null,
    detailModalOpen: false,
    detailModalData: null,
    currentPageNumber: parseInt(activePage),
    itemLimit: 10,
    invoiceCount: 0,
    currentStatus: 0,
    invoice_order_by: PAYMENT_ORDER_BY.desInvoiceDate,
    editModalToggle: false,
    openDeletePrompt: false,
    delete_invoice_info_id: null,
    showFilterDrawer: false,
    filters: null
  });

  const [filterState, setFilterState] = useState<IFilterState>({
    supplier: null,
    orderId: null,
    startDate: null,
    endDate: null,
    entity: null,
    searchText: null,
    isSearchLoading: false,
    excludeZeroOutstanding: false
  });

  const {
    supplier,
    orderId,
    startDate,
    endDate,
    searchText,
    isSearchLoading,
    entity,
    excludeZeroOutstanding
  } = filterState;

  const {
    isLoading,
    tableData,
    detailModalOpen,
    detailModalData,
    currentPageNumber,
    itemLimit,
    invoiceCount,
    currentStatus,
    invoice_order_by,
    editModalToggle,
    openDeletePrompt,
    delete_invoice_info_id,
    showFilterDrawer,
    filters
  } = invoicesState;

  const fetchInvoiceTableData = async (params: IInvoiceTableParams, order_id?: string) => {
    setInvoicesState((prev: any) => ({ ...prev, isLoading: true }));
    const response = await fetchInvoiceList(params);
    if (response?.success) {
      setInvoicesState((prev: any) => ({
        ...prev,
        tableData: response?.data?.results ?? [],
        invoiceCount: response?.data?.count ?? 0,
        isLoading: false
      }));
      props.setInvoiceKeys &&
        props.setInvoiceKeys((prevState: IInvoiceKeys) => {
          return {
            ...prevState,
            totalAdditionalChargesOrDiscounts:
              response?.data?.total_additional_charges_or_discounts_paid,
            totalTcsPaid: response?.data?.total_tcs_paid,
            totalTdsPaid: response?.data?.total_tds_paid,
            totalGrossAmount: response?.data?.gross_amount,
            totalNetAmount: response?.data?.net_payable,
            totalAmountPaid: response?.data?.total_amount_paid
          };
        });
    } else {
      notify({
        severity: 'error',
        message: response?.message
      });
      setInvoicesState((prev: any) => ({ ...prev, isLoading: false }));
    }
  };

  useEffect(() => {
    fetchInvoiceTableData({
      page: currentPageNumber,
      order_by: invoice_order_by,
      order_id: orderId?.order_id ? orderId?.order_id : props?.order_id ?? null,
      supplier_id: supplier?.supplier_id ?? null,
      search_text: searchText,
      lower_bound_date: startDate ? `${moment(startDate).format('YYYY-MM-DD')}` : null,
      upper_bound_date: endDate ? `${moment(endDate).format('YYYY-MM-DD')}` : null,
      entity_id: entity?.entity_id ?? null,
      non_zero_due_amount: excludeZeroOutstanding
    });
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: currentPageNumber.toString()
    });
  }, [
    currentPageNumber,
    currentStatus,
    invoice_order_by,
    supplier,
    orderId,
    startDate,
    endDate,
    entity,
    excludeZeroOutstanding
  ]);

  const sortDocument = (status: number) => {
    switch (status) {
      case 0:
        setInvoicesState((prev: any) => ({
          ...prev,
          invoice_order_by: null,
          currentPageNumber: 1
        }));
        break;
      case 1:
        setInvoicesState((prev: any) => ({
          ...prev,
          invoice_order_by: PAYMENT_ORDER_BY.ascInvoiceDate,
          currentPageNumber: 1
        }));
        break;
      case 2:
        setInvoicesState((prev: any) => ({
          ...prev,
          invoice_order_by: PAYMENT_ORDER_BY.desInvoiceDate,
          currentPageNumber: 1
        }));
        break;
      default:
        setInvoicesState((prev: any) => ({
          ...prev,
          invoice_order_by: null,
          currentPageNumber: 1
        }));
    }

    setInvoicesState((prevState: any) => ({
      ...prevState,
      currentStatus: status
    }));
  };

  const [columnData, rowData] = useMemo(() => {
    const column: Array<any> = [
      {
        Header: () => (
          <div
            className={css.sortTableHeader}
            onClick={() => {
              const newStatus = currentStatus === 0 ? 1 : currentStatus === 1 ? 2 : 0;
              sortDocument(newStatus);
            }}>
            <ToolTip
              title={
                <div>
                  <div>Invoice Number</div>
                  <div>Invoice Date</div>
                </div>
              }>
              <div>
                <IconNode src={Images.alertGrey} alt="eye icon" />
              </div>
            </ToolTip>
            <span>Invoice Details</span>
            <IconNode src={Images.sort} alt="document" width={16} height={16} />
          </div>
        ),
        accessor: 'invoice',
        Cell: (props: CellProps<any>) => {
          const { value } = props;
          return (
            <>
              <Typography variant="p">{value?.invoice_number}</Typography>
              <Typography variant="p">{getFormattedDate(value?.invoice_date ?? '')}</Typography>
            </>
          );
        }
      },
      {
        Header: 'Document Type',
        accessor: 'document_type_display'
      },
      {
        Header: 'Supplier',
        accessor: 'supplier'
      },
      {
        Header: 'Document Name',
        accessor: 'name'
      },
      {
        Header: 'Gross Amount',
        accessor: 'gross_amount'
      },
      {
        Header: 'Due Amount',
        accessor: 'due_amount'
      },
      // {
      //   Header: 'Tags',
      //   accessor: 'internal_tags',
      //   Cell: (props: CellProps<any>) => {
      //     const { value } = props;
      //     return (
      //       <div className={css.chipGroup}>
      //         {value?.length ? (
      //           value?.length > TAG_LIMIT_VIEW ? (
      //             <>
      //               {value.slice(0, TAG_LIMIT_VIEW)?.map((tag: any) => (
      //                 <Chip key={tag} label={tag} className={css.chip} />
      //               ))}
      //               <ToolTip title={value.slice(TAG_LIMIT_VIEW).join(', ')} placement="right">
      //                 <div>
      //                   <Chip label={`+ ${value.length - TAG_LIMIT_VIEW}`} className={css.chip} />
      //                 </div>
      //               </ToolTip>
      //             </>
      //           ) : (
      //             value?.map((tag: any) => <Chip key={tag} label={tag} className={css.chip} />)
      //           )
      //         ) : (
      //           <>-</>
      //         )}
      //       </div>
      //     );
      //   }
      // },
      {
        Header: '',
        accessor: 'action',
        Cell: (props: CellProps<any>) => {
          const { value } = props;
          return (
            <>
              <DocumentRow.View
                showEyeIconWhenDocumentMissing={true}
                document={value.invoice_document}
                title=""
                openViaSlug
              />
            </>
          );
        }
      },
      {
        Header: '',
        accessor: 'view',
        Cell: (props: CellProps<any>) => {
          const { value } = props;
          const { is_editable } = value;
          return (
            <div className={css.viewEditDeleteButton}>
              <Button
                variant="text"
                onClick={() =>
                  setInvoicesState((prevState: any) => ({
                    ...prevState,
                    detailModalOpen: true,
                    detailModalData: value
                  }))
                }>
                View
              </Button>
              <Divider className={css.divider} />
              <IconNode
                src={is_editable ? Images.editRed : Images.editGrey}
                alt="edit icon"
                onClick={() =>
                  is_editable
                    ? setInvoicesState((prevState: any) => ({
                        ...prevState,
                        editModalToggle: true,
                        detailModalData: value
                      }))
                    : null
                }
                className={css.editIcon}
              />
              <IconNode
                src={is_editable ? Images.deleteRed : Images.delete}
                alt="Delete icon"
                onClick={() =>
                  is_editable
                    ? setInvoicesState((prevState: IInvoiceState) => ({
                        ...prevState,
                        openDeletePrompt: true,
                        delete_invoice_info_id: value?.invoice_info_id
                      }))
                    : null
                }
                className={css.deleteIcon}
              />
            </div>
          );
        }
      }
    ];

    const row: Array<any> = tableData?.length
      ? tableData?.map((item: any) => {
          const dueAmount =
            (item?.gross_invoice_amount ?? 0) -
            (item?.total_amount_paid ?? 0) -
            (item?.total_child_invoices_gross_amount ?? 0);

          const formattedGrossAmount = getCommaSeperatedCurrencyAmount(
            item.currency,
            item?.gross_invoice_amount ?? 0
          );
          const formattedDueAmount = getCommaSeperatedCurrencyAmount(item.currency, dueAmount);

          return {
            invoice: item ?? null,
            document_type_display: item?.document_type_display ?? '-',
            name: item?.name ?? '-',
            net_amount_payable: `${item.currency} ${calculateNetAmount({
              gross_invoice_amount: item?.gross_invoice_amount ?? 0,
              tds_amount: item?.tds_amount ?? 0,
              tcs_amount: item?.tcs_amount ?? 0,
              additional_charges_or_discounts: item?.additional_charges_or_discounts ?? 0
            })}`,
            gross_amount: formattedGrossAmount,
            due_amount: formattedDueAmount,
            internal_tags: item.internal_tags?.length ? item.internal_tags : '-',
            action: item ?? null,
            view: item ?? null,
            supplier: item?.supplier_name ?? '-'
          };
        })
      : [];

    return [column, row];
  }, [tableData]);

  const changePage = (newPage: number) => {
    setInvoicesState((prev: any) => ({ ...prev, currentPageNumber: newPage }));
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: newPage.toString()
    });
  };

  const validatePageChange = (event: any, value: number) => {
    if (isNaN(value)) return;
    if (value === 0 || value > Math.ceil(invoiceCount / itemLimit)) return;
    changePage(value);
  };

  const fetchSearchResult = async (searchText: string) => {
    fetchInvoiceTableData({
      page: currentPageNumber,
      order_by: invoice_order_by,
      order_id: orderId?.order_id ? orderId?.order_id : props?.order_id ?? null,
      supplier_id: supplier?.supplier_id ?? null,
      search_text: searchText,
      lower_bound_date: startDate ? `${moment(startDate).format('YYYY-MM-DD')}` : null,
      upper_bound_date: endDate ? `${moment(endDate).format('YYYY-MM-DD')}` : null,
      entity_id: entity?.entity_id ?? null,
      non_zero_due_amount: excludeZeroOutstanding
    });
  };

  const handleOrderSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    setFilterState((prevState: any) => ({
      ...prevState,
      searchText: value.trim(),
      isSearchLoading: true
    }));
    debounceSearch.current?.cancel();
    debounceSearch.current = debounce(fetchSearchResult, 800);
    debounceSearch.current(value);
  };

  const deleteInvoiceDocument = async () => {
    const response = await deleteInvoice(delete_invoice_info_id ?? '');
    if (response.success) {
      notify({
        severity: 'success',
        message: response.data
      });
      fetchInvoiceTableData({
        page: currentPageNumber,
        order_by: invoice_order_by,
        order_id: orderId?.order_id ? orderId?.order_id : props?.order_id ?? null,
        supplier_id: supplier?.supplier_id ?? null,
        search_text: searchText,
        lower_bound_date: startDate ? `${moment(startDate).format('YYYY-MM-DD')}` : null,
        upper_bound_date: endDate ? `${moment(endDate).format('YYYY-MM-DD')}` : null,
        entity_id: entity?.entity_id ?? null,
        non_zero_due_amount: excludeZeroOutstanding
      });
    } else if (response.error) {
      notify({
        severity: 'error',
        message: response.error
      });
    }
    setInvoicesState((prevState: any) => ({
      ...prevState,
      openDeletePrompt: false,
      delete_invoice_info_id: null
    }));
  };

  const handleFilterClick = () => {
    setInvoicesState((prevState: any) => ({
      ...prevState,
      showFilterDrawer: !showFilterDrawer
    }));
  };

  const onClose = () => {
    setInvoicesState((prevState: IInvoiceState) => ({
      ...prevState,
      editModalToggle: false,
      detailModalData: null
    }));
  };

  const handleFilterApply = (data: any) => {
    setFilterState((prevState: any) => ({
      ...prevState,
      supplier: data?.selectedSupplier,
      orderId: data?.selectedOrderId,
      startDate: data?.selectedStartDate,
      endDate: data?.selectedEndDate,
      entity: data?.selectedEntity,
      excludeZeroOutstanding: data?.excludeZeroOutstanding
    }));
    setInvoicesState((prevState: any) => ({
      ...prevState,
      filters: data,
      showFilterDrawer: false,
      currentPageNumber: 1
    }));
  };

  const handleClearFilter = () => {
    setFilterState((prevState: any) => ({
      ...prevState,
      supplier: null,
      orderId: null,
      startDate: null,
      endDate: null,
      entity: null,
      searchText: null,
      excludeZeroOutstanding: false
    }));
    setInvoicesState((prevState: any) => ({
      ...prevState,
      filters: null,
      currentPageNumber: 1
    }));
  };

  return (
    <div className={css.tableWrapper}>
      <div className={css.filterWrapper}>
        <div className={css.searchBar}>
          <SearchBar
            isLoading={isLoading}
            placeholder="Search"
            className={css.search}
            onChange={handleOrderSearch}
          />
          <div>
            <ToolTip title={`Search By: Invoice Number`} placement="right">
              <div>
                <IconNode src={Images.alertGrey} alt="eye icon" />
              </div>
            </ToolTip>
          </div>
        </div>
        <div className={css.filterButtonWrapper}>
          <Button
            className={css.filterButton}
            variant="outlined"
            startIcon={<IconNode src={Images.filter} alt="filter icon" />}
            onClick={() => {
              handleFilterClick();
            }}>
            {`Filter ${
              getValidEntriesLength(filterState) > 0
                ? `(${getValidEntriesLength(filterState)})`
                : ''
            }`}
          </Button>
          {getValidEntriesLength(filterState) > 0 && (
            <Button
              variant="text"
              onClick={() => {
                handleClearFilter();
              }}
              startIcon={<IconNode src={Images.crossRed} alt="clear icon" />}>
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <TableSkeleton colsNum={9} rowsNum={10} />
      ) : rowData.length ? (
        <DataGrid columns={columnData} data={rowData} tableStyle={{ width: '100%' }} />
      ) : (
        <ListPlaceholder
          title="No Invoice available"
          supportingText="You have not created any invoice yet."
        />
      )}

      {detailModalOpen && (
        <DetailModal
          data={detailModalData}
          open={detailModalOpen}
          onClose={() =>
            setInvoicesState((prevState: any) => ({
              ...prevState,
              detailModalOpen: false,
              detailModalData: null
            }))
          }
        />
      )}

      {!isLoading && (
        <PaginationScroller
          variant="text"
          defaultPage={1}
          count={invoiceCount}
          pageLimit={itemLimit}
          page={currentPageNumber}
          onChange={validatePageChange}
        />
      )}

      {editModalToggle && (
        <UploadOrderDocument
          open={editModalToggle}
          onClose={onClose}
          orderId={props?.order_id}
          editMode={'USER_GENERATED'}
          documentData={detailModalData}
          getDocumentData={fetchInvoiceTableData}
          uploadType="finance"
        />
      )}

      <DeletePrompt
        open={openDeletePrompt}
        onClose={() =>
          setInvoicesState((prevState: IInvoiceState) => ({
            ...prevState,
            openDeletePrompt: false
          }))
        }
        onConfirm={deleteInvoiceDocument}
      />

      <Drawer
        open={showFilterDrawer}
        anchor="right"
        onClose={() => {
          handleFilterClick();
        }}>
        <FinanceFilterDrawer
          value={filters}
          onApply={handleFilterApply}
          onClose={handleFilterClick}
        />
      </Drawer>
    </div>
  );
};

export default InvoicesTab;
